import * as _ from 'lodash'
import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_ProductAttributeMapRow,
    WB_ProductAttributeMapValueRelationshipRow,
    WB_ProductAttributeMapExternalRelationshipRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbProductAttributeMap} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Product_Attribute_Map,
    esWB_Product_Attribute_Map_External_Relationship,
    esWB_Product_Attribute_Map_Value_Relationship, esWB_Product_Group_Relationship
} from "../PA.Core/PAdbSchema";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {ASync} from "../PA.Core/Basic/ASync";
import {PASearch} from "../PA.Core/PASearch";
import {PAEnvironment} from "../PA.Core/PAEnvironment";

export class ProductAttributeMapBO implements IBusinessObject
{
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_ProductAttributeMap: WB_ProductAttributeMapRow;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Product Attribute Map'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_ProductAttributeMap = null;
    }

    get WB_ProductAttributeMap(): WB_ProductAttributeMapRow {
        return this.drWB_ProductAttributeMap;
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Product Attribute Map");
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, error)=>{

            self.LoadOptions = options || {};
            if('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if(this.dbContext == null) {
                let sError = "Unable to determine dbContext for new ProductAttributeMap.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_Product_Attribute_Map)
                .then((newProductAttributeMapRow: WB_ProductAttributeMapRow)=>{
                    newProductAttributeMapRow.setDefaults();
                    self.drWB_ProductAttributeMap = newProductAttributeMapRow;
                    self.dbContext.add(esWB_Product_Attribute_Map, newProductAttributeMapRow);
                    self.Identity.setAll(self.WB_ProductAttributeMap.cif.WB_PRDATTRMAP_ID, self.WB_ProductAttributeMap.cif.ATTRIB_MAP_ID, self.WB_ProductAttributeMap.cif.ATTRIB_MAP_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_ProductAttributeMap, 'WB_PRDATTRMAP_ID','ATTRIB_MAP_ID','ATTRIB_MAP_NAME');

                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err);
                    error(err)
                })
        });

        return this._OnLoadComplete;
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error)=>{

            if (self.bIsLoaded) {
                let sError = "Product Attribute Map already loaded.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            self.LoadOptions = options || {};

            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if(self.dbContext == null)
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                let sError = "Unable to determine dbContext for Product Attribute Map.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            if('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any> self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = ProductAttributeMapBO.createRecordResolver({dbContext: self.dbContext});
                recordResolverLoadPromise = ProductAttributeMapBO.setupRecordResolver(recordResolver, [anId])
            }

            recordResolverLoadPromise
                .then(function () {
                    let newProductAttributeMapRow: any = recordResolver.get(esWB_Product_Attribute_Map, anId);

                    if(_.isEmpty(newProductAttributeMapRow))
                    {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }

                    self.drWB_ProductAttributeMap = newProductAttributeMapRow;
                    self.Identity.setAll(self.WB_ProductAttributeMap.cif.WB_PRDATTRMAP_ID, self.WB_ProductAttributeMap.cif.ATTRIB_MAP_ID, self.WB_ProductAttributeMap.cif.ATTRIB_MAP_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_ProductAttributeMap, 'WB_PRDATTRMAP_ID','ATTRIB_MAP_ID','ATTRIB_MAP_NAME');
                })
                .then((newProductAttributeMapRow:any) => {
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err:any)=>{
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });

        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    Validate(options?: any): Promise<IBusinessObject> {

        let self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start("Validating " + self.WB_ProductAttributeMap.cif.ATTRIB_MAP_NAME + "(" + self.WB_ProductAttributeMap.cif.ATTRIB_MAP_ID + ")");
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == undefined) {
                let sError = "Unable to determine dbContext to Validate ProductAttributeMap.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }

            self._bIsValidated = true;
            self.ValidateProgressProvider.finish();
            resolve(self);
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    private SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        const self = this;
        const wbProductAttributeMap = <IwbProductAttributeMap>self.drWB_ProductAttributeMap;
        this.SaveProgressProvider.start("Saving " + wbProductAttributeMap.ATTRIB_MAP_NAME + "(" + wbProductAttributeMap.ATTRIB_MAP_ID + ")");

        return new Promise<IBusinessObject>((resolve, reject)=>{
            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // We could search the tree if there is any record where store isn't set.

            if (self.dbContext == null) {
                self._bIsSaved = false;
                let sError = "Unable to determine dbContext to Save ProductAttributeMap.";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return
            }

            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            saveFunction(esWB_Product_Attribute_Map, self.drWB_ProductAttributeMap, {})
                .then((ignored)=>{
                    let tasks: Promise<any>[] = [];
                    tasks.push(saveFunction(esWB_Product_Attribute_Map_External_Relationship, self.drWB_ProductAttributeMap.WB_ProductAttributeMapExternalRelationshipRows));
                    tasks.push(saveFunction(esWB_Product_Attribute_Map_Value_Relationship, self.drWB_ProductAttributeMap.WB_ProductAttributeMapValueRelationshipRows));

                    return Promise.all(tasks);
                })
                .then((ignored)=>{
                    if (bBatch) {
                        self.dbContext.addBatchCompleteListener(()=> {
                            self._bIsSaved = true;
                        })
                    } else {
                        self._bIsSaved = true;
                    }
                    self.SaveProgressProvider.finish();
                    resolve(self);
                })
                .catch((err) => {
                    self.SaveProgressProvider.exception(err);
                    reject(self);
                })
        })
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbProductAttributeMap = <IwbProductAttributeMap>self.drWB_ProductAttributeMap;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbProductAttributeMap.ATTRIB_MAP_NAME + "(" + wbProductAttributeMap.ATTRIB_MAP_ID + ")");

        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the Customer

            // 2. If Valid, mark all the records for deletion.
            self.drWB_ProductAttributeMap.markDeleted();

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);

                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err);
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    addExtAttrMap(): Promise<IBusinessObject> {
        const self = this;
        return new Promise<IBusinessObject>((resolve, reject)=>{

            self.dbContext.createRecordWithKey(esWB_Product_Attribute_Map_External_Relationship)
                .then((aPrdAttrMapExtRelRow: WB_ProductAttributeMapExternalRelationshipRow)=>{
                    aPrdAttrMapExtRelRow.setDefaults();
                    aPrdAttrMapExtRelRow.cif.WB_PRDATTRMAP_ID = self.drWB_ProductAttributeMap.cif.WB_PRDATTRMAP_ID;
                    aPrdAttrMapExtRelRow.WB_ProductAttributeMapRow = self.drWB_ProductAttributeMap;
                    self.dbContext.add(esWB_Product_Attribute_Map_External_Relationship, aPrdAttrMapExtRelRow);
                    resolve(self)
                })
                .catch((err)=>{
                    reject(err)
                })
        })
    }

    removeExtAttrMap(aWB_PrdAttrMapExtR_ID: number): Promise<IBusinessObject> {
        const self = this;

        return new Promise<IBusinessObject>((resolve, reject)=>{
            _.forEach(self.drWB_ProductAttributeMap.WB_ProductAttributeMapExternalRelationshipRows, (aPrdAttrMapExtR: WB_ProductAttributeMapExternalRelationshipRow)=>{
                if(aPrdAttrMapExtR.isActive && aPrdAttrMapExtR.cif.WB_PRDATTRMAPEXTR_ID == aWB_PrdAttrMapExtR_ID){
                    aPrdAttrMapExtR.markDeleted();
                }
            });
            resolve(self)
        })
    }

    addValAttrMap(): Promise<IBusinessObject> {
        const self = this;
        return new Promise<IBusinessObject>((resolve, reject)=>{

            self.dbContext.createRecordWithKey(esWB_Product_Attribute_Map_Value_Relationship)
                .then((aPrdAttrValRelRow: WB_ProductAttributeMapValueRelationshipRow)=>{
                    aPrdAttrValRelRow.setDefaults();
                    aPrdAttrValRelRow.cif.WB_PRDATTRMAP_ID = self.drWB_ProductAttributeMap.cif.WB_PRDATTRMAP_ID;
                    aPrdAttrValRelRow.WB_ProductAttributeMapRow = self.drWB_ProductAttributeMap;
                    self.dbContext.add(esWB_Product_Attribute_Map_Value_Relationship, aPrdAttrValRelRow);
                    resolve(self)
                })
                .catch((err)=>{
                    reject(err)
                })
        })
    }

    removeValAttrMap(aWB_PrdAttrMapValR_ID: number): Promise<IBusinessObject> {
        const self = this;

        return new Promise<IBusinessObject>((resolve, reject)=>{
            _.forEach(self.drWB_ProductAttributeMap.WB_ProductAttributeMapValueRelationshipRows, (aPrdAttrMapValR: WB_ProductAttributeMapValueRelationshipRow)=>{
                if(aPrdAttrMapValR.isActive && aPrdAttrMapValR.cif.WB_PRDATTRMAPVALUER_ID == aWB_PrdAttrMapValR_ID){
                    aPrdAttrMapValR.markDeleted();
                }
            });
            resolve(self)
        })
    }

    async addDistinctAttrValues(): Promise<IBusinessObject> {
        const self = this;

        let whereFieldSpecJSON: any = {
            EXTPRD_ATR_ID: {type: "number"},
            EXTERNAL_PRODUCT_ID: {type: "string"},
            SOURCE_ID: {type: "string"},
            ATTRIBUTE_ID: {type: "string"},
            ATTRIBUTE_VALUE: {type: "string"},
        };

        if(_.isEmpty(self.WB_ProductAttributeMap.cif.ATTRIBUTE_ID))
            return;

        let whereConditionJSON: any = {ATTRIBUTE_ID: self.WB_ProductAttributeMap.cif.ATTRIBUTE_ID};

        if(self.WB_ProductAttributeMap.cif.SOURCE_ID != 'All')
            whereConditionJSON = {...whereConditionJSON, SOURCE_ID: self.WB_ProductAttributeMap.cif.SOURCE_ID};

        let dstnctfldNames = await PASearch.find('EXTPRD_ATR', whereConditionJSON, whereFieldSpecJSON,
            [],
            0, ['ATTRIBUTE_VALUE']);

        if(_.isEmpty(dstnctfldNames)) {
            return
        }

        let existValues = _.keyBy(_.filter(self.WB_ProductAttributeMap.WB_ProductAttributeMapValueRelationshipRows, a => a.isActive),
                            a => a.cif.ATTRIBUTE_VALUE);

        for(let aRow of dstnctfldNames){
            let val = aRow.ATTRIBUTE_VALUE;

            if(val in existValues)
                continue;

            let newRow = <WB_ProductAttributeMapValueRelationshipRow> await self.dbContext.createRecordWithKey(esWB_Product_Attribute_Map_Value_Relationship);

            newRow.cif.WB_PRDATTRMAP_ID = self.drWB_ProductAttributeMap.cif.WB_PRDATTRMAP_ID;
            newRow.cif.ATTRIBUTE_VALUE = val;
            newRow.cif.TARGET_VALUE = val;
            self.dbContext.add(esWB_Product_Attribute_Map_Value_Relationship, newRow);
        }

    }

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const product_attribute_map_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        };

        const aRecResolver = new ESRecordResolver(dbContext, product_attribute_map_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Product_Attribute_Map,ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold>
}

PAEnvironment.registerBusinessObjectClass( ProductAttributeMapBO , "ProductAttributeMapBO", "Product Attribute Map")